<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMapGetter } from 'dashboard/composables/store';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import getWootAxios from 'dashboard/helper/getWootAxios';

const { updateUISettings } = useUISettings();
const axios = getWootAxios();

const currentChat = useMapGetter('getSelectedChat');

const { uiSettings } = useUISettings();
const isContactSidebarOpen = computed(
  () => uiSettings.value.is_contact_sidebar_open
);
const isAiAssistantOpen = computed(() => uiSettings.value.is_ai_assistant_open);

const showAilabAiAssistantTab = ref(false);
const ailabAssistantInactive = ref(false);

const isAilabSelected = computed(() => {
  return isAiAssistantOpen.value && showAilabAiAssistantTab.value;
});

const currentAccountIdFromPath = () => {
  try {
    return window.location.pathname.split('/')[3];
  } catch (e) {
    return null;
  }
};

const checkBotMode = async () => {
  try {
    const conversationId = currentChat.value?.id;
    if (!conversationId) {
      showAilabAiAssistantTab.value = false;
      return;
    }

    const response = await ConversationApi.getInboxAssistant(conversationId);
    const assistant = response?.data?.assistant;
    const sidebarKey = assistant?.wlab_sidebar_key;
    const hasAilabAssistant =
      Boolean(assistant?.id) &&
      ['ai_closer', 'ai_gamer', 'ai_studio'].includes(
        (sidebarKey || 'ai_closer').toString()
      );

    ailabAssistantInactive.value = false;
    if (hasAilabAssistant) {
      const accountId = currentAccountIdFromPath();
      if (accountId) {
        try {
          const resp = await axios.get(
            `/api/v1/accounts/${accountId}/ailab/assistants/${assistant.id}/ai_closer/config`
          );
          const status = (resp.data?.status || '').toString();
          ailabAssistantInactive.value = status !== 'active';
        } catch (e) {
          ailabAssistantInactive.value = true;
        }
      }
    }

    showAilabAiAssistantTab.value =
      hasAilabAssistant && !ailabAssistantInactive.value;
  } catch (error) {
    showAilabAiAssistantTab.value = false;
    ailabAssistantInactive.value = false;
  }
};

const toggleConversationSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: !isContactSidebarOpen.value,
    is_copilot_panel_open: false,
    is_ai_assistant_open: false,
  });
};

const handleConversationSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: true,
    is_copilot_panel_open: false,
    is_ai_assistant_open: false,
  });
};

const handleAilabAiAssistantToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
    is_ai_assistant_open: true,
    ai_assistant_provider: 'ailab',
  });
};

useKeyboardEvents({
  'Alt+KeyO': {
    action: toggleConversationSidebarToggle,
  },
});

onMounted(() => {
  checkBotMode();
});

watch(
  () => currentChat.value?.id,
  () => {
    checkBotMode();
  }
);
</script>

<template>
  <div
    class="flex flex-col justify-center items-center absolute top-36 xl:top-24 ltr:right-2 rtl:left-2 bg-n-solid-2 border border-n-weak rounded-full gap-2 p-1 z-30 md:z-50"
  >
    <Button
      v-tooltip.top="$t('CONVERSATION.SIDEBAR.CONTACT')"
      ghost
      slate
      sm
      class="!rounded-full"
      :class="{
        'bg-n-alpha-2 ring-2 ring-woot-500/70 ring-offset-2 ring-offset-n-solid-2':
          isContactSidebarOpen,
      }"
      icon="i-ph-user-bold"
      @click="handleConversationSidebarToggle"
    />
    <Button
      v-if="showAilabAiAssistantTab"
      v-tooltip.bottom="'AI Studio / AI Closer / AI gamer'"
      ghost
      slate
      class="!rounded-full"
      :class="{
        'bg-n-alpha-2 !text-woot-500 ring-2 ring-woot-500/70 ring-offset-2 ring-offset-n-solid-2':
          isAilabSelected,
      }"
      sm
      icon="i-lucide-bot"
      @click="handleAilabAiAssistantToggle"
    />
  </div>
</template>
